import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { GoogleMap, useJsApiLoader, MarkerF, PolylineF } from "@react-google-maps/api";
import { MapPin, Loader2, Check, AlertTriangle } from "lucide-react";
import api from "../api";
import { useLang } from "../context/LanguageContext";
import { distanceKm } from "../utils/geo";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Reached from the "Move my pin here" button in the wrong-pin email. The one-use token in the
// link is the permission, but nothing moves until the shop taps Confirm — link previewers and
// mail scanners fetch URLs in the background, so acting on the visit itself would move pins
// nobody asked to move.
export default function FixPin() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { isLoaded } = useJsApiLoader({ id: "gmap-script", googleMapsApiKey: GKEY || "" });
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setError(t("fx.noCode")); return; }
    try {
      setInfo((await api.get(`/pin-fix/${encodeURIComponent(token)}`)).data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("fx.invalid"));
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/pin-fix/${encodeURIComponent(token)}`);
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || t("fx.failed"));
    } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="max-w-xl mx-auto px-4 py-10" data-testid="fix-pin-page">
      <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">{children}</div>
      <p className="text-xs text-muted-foreground text-center mt-4">
        <Link to="/account" className="font-semibold text-[#16a34a] hover:underline">{t("fx.openAccount")}</Link> {t("fx.byHand")}
      </p>
    </div>
  );

  if (error) {
    return shell(
      <div data-testid="fix-pin-error">
        <h1 className="text-xl font-extrabold text-foreground flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-500" /> {t("fx.cantUse")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </div>
    );
  }

  if (!info) {
    return shell(<p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t("fx.checking")}</p>);
  }

  if (done || info.state === "used") {
    return shell(
      <div data-testid="fix-pin-done">
        <h1 className="text-xl font-extrabold text-foreground flex items-center gap-2">
          <Check size={20} className="text-[#16a34a]" /> {t("fx.moved")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t("fx.movedNote", { branch: info.label || t("fx.yourPin"), address: info.address || t("fx.yourListing") })}
        </p>
      </div>
    );
  }

  if (info.state === "expired") {
    return shell(
      <div data-testid="fix-pin-expired">
        <h1 className="text-xl font-extrabold text-foreground flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-500" /> {t("fx.expired")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t("fx.expiredNote")}
        </p>
      </div>
    );
  }

  const from = info.from || {};
  const to = info.to || {};
  const km = from.lat != null && to.lat != null ? distanceKm(from.lat, from.lng, to.lat, to.lng) : null;

  return shell(
    <div>
      <h1 className="text-xl font-extrabold text-foreground flex items-center gap-2">
        <MapPin size={20} className="text-[#16a34a]" /> {t("fx.title")}
      </h1>
      <p className="text-sm text-muted-foreground mt-2" data-testid="fix-pin-summary">
        {t("fx.summary", { business: info.businessName, branch: info.label || t("fx.aLocation"),
          distance: km != null ? km.toFixed(1) : "?", listing: info.listing,
          address: info.address ? `, ${info.address}` : "" })}
      </p>

      {isLoaded && from.lat != null && to.lat != null && (
        <div className="mt-4 rounded-xl overflow-hidden border border-border">
          <GoogleMap mapContainerStyle={{ width: "100%", height: "260px" }}
            center={{ lat: (from.lat + to.lat) / 2, lng: (from.lng + to.lng) / 2 }} zoom={11}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
            onLoad={(map) => {
              const b = new window.google.maps.LatLngBounds();
              b.extend({ lat: from.lat, lng: from.lng });
              b.extend({ lat: to.lat, lng: to.lng });
              map.fitBounds(b, 50);
            }}>
            <MarkerF position={{ lat: from.lat, lng: from.lng }} label={t("fx.now")}
              icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#dc2626", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }} />
            <MarkerF position={{ lat: to.lat, lng: to.lng }} label={t("fx.listing")}
              icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#16a34a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }} />
            <PolylineF path={[{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }]}
              options={{ strokeColor: "#b45309", strokeWeight: 3, strokeOpacity: 0.7 }} />
          </GoogleMap>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-3">
        {t("fx.legend")}
      </p>

      <button onClick={confirm} disabled={busy} data-testid="fix-pin-confirm-btn"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-3 rounded-xl transition-colors">
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />} {t("fx.confirm")}
      </button>
    </div>
  );
}
